// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AssetLocationFilterMenu } from '@/asset/components/asset-location-filter-menu';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { EducationalTooltip } from '@/components/educational-tooltip';
import {
  FilterOption,
  FilterToggleGroup,
} from '@/components/filter-toggle-group';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { TableControlContainer } from '@/components/table-control-container';
import {
  AddRounded,
  ClearRounded,
  DoNotDisturbAltOutlined,
  EditOutlined,
  InfoOutlined,
  KeyboardTabOutlined,
  ListRounded,
  LocalShippingOutlined,
  PendingOutlined,
  SettingsEthernetOutlined,
} from '@/constants/hardfin-standard-icons';
import { LocationCompanyType } from '@/constants/location-company-type';
import { SEARCH_TERM_MAXIMUM_CHARACTERS } from '@/constants/search-term';
import { Sort } from '@/constants/sort';
import { FulfillmentModifierFilter } from '@/fulfillment/components/fulfillment-modifier-filter';
import { FulfillmentTable } from '@/fulfillment/components/fulfillment-table';
import { FulfillmentTableSkeleton } from '@/fulfillment/components/fulfillment-table-skeleton';
import {
  type FulfillmentOrderPhase,
  type FulfillmentOrderSubstatus,
  PHASES_WITH_MODIFIERS,
} from '@/fulfillment/constants/fulfillment-order-status';
import {
  FulfillmentSearchListing,
  FulfillmentSortProperty,
} from '@/fulfillment/hooks/use-fulfillments';
import { useDisclosure } from '@/hooks/use-disclosure';
import { CreateOrderDialog } from '@/order/components/create-order-dialog';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { debounce, omit } from 'lodash-es';
import {
  ChangeEventHandler,
  Suspense,
  useEffect,
  useMemo,
  useRef,
} from 'react';

/**
 * Navigation options for the fulfillment listing.
 */
interface NavigateOptions {
  search?: Partial<FulfillmentSearchListing> & Record<string, unknown>;
}

interface FulfillmentListingProps {
  currentStatus: FulfillmentOrderPhase | 'all';
  searchParameters: FulfillmentSearchListing;
  sort: Sort<FulfillmentSortProperty>;
  onSort: (property: FulfillmentSortProperty) => void;
  navigateFn: (options: NavigateOptions) => void;
  forProjectId?: string;
  hideChrome?: boolean;
  enableRowExpansion?: boolean;
  initialProjectID?: string;
  /**
   * When true, locks the project field on the embedded create-order dialog.
   * Set when the listing lives inside a project-scoped page so the user
   * can't accidentally retarget the FO they're creating.
   */
  disableProjectChange?: boolean;
}
const STATUS_FILTER_OPTIONS: FilterOption<FulfillmentOrderPhase | 'all'>[] = [
  {
    value: 'all',
    label: 'All',
    icon: <ListRounded fontSize="small" />,
  },
  {
    value: 'draft',
    label: 'Draft',
    icon: <EditOutlined fontSize="small" />,
  },
  {
    value: 'awaiting_3pl',
    label: 'Awaiting 3PL',
    icon: <SettingsEthernetOutlined fontSize="small" />,
  },
  {
    value: 'open',
    label: 'Open',
    icon: <PendingOutlined fontSize="small" />,
  },
  {
    value: 'shipped',
    label: 'Shipped',
    icon: <LocalShippingOutlined fontSize="small" />,
  },
  {
    value: 'delivered',
    label: 'Delivered',
    icon: <KeyboardTabOutlined fontSize="small" />,
  },
  {
    value: 'cancelled',
    label: 'Cancelled',
    icon: <DoNotDisturbAltOutlined fontSize="small" />,
  },
];

export function FulfillmentListing(props: FulfillmentListingProps) {
  const { navigateFn, searchParameters, currentStatus, sort, onSort } = props;
  const searchContainer = useRef<HTMLInputElement>(null);
  const searchParametersRef = useRef(searchParameters);
  searchParametersRef.current = searchParameters;
  const createDialog = useDisclosure();
  const infoTooltip = useDisclosure();
  const infoButtonRef = useRef<HTMLButtonElement>(null);
  const { user } = useCurrentUser();

  // Filter status options based on organization's third party settings
  const statusFilterOptions = useMemo(() => {
    const usesThirdParty = user.organization.usesThirdPartyFulfillment;
    if (usesThirdParty) {
      return STATUS_FILTER_OPTIONS;
    }
    return STATUS_FILTER_OPTIONS.filter((opt) => opt.value !== 'awaiting_3pl');
  }, [user.organization.usesThirdPartyFulfillment]);

  /**
   * Debounced search query handler.
   */
  const updateQuery = useMemo(() => {
    return debounce((query: string) => {
      if (query.length === 0) {
        navigateFn({
          search: { ...omit(searchParametersRef.current, 'page', 'query') },
        });
        return;
      }
      navigateFn({
        search: { ...omit(searchParametersRef.current, 'page'), query },
      });
    }, 300);
  }, [navigateFn]);

  // Cancel on unmount
  useEffect(() => {
    return () => {
      updateQuery.cancel();
    };
  }, [updateQuery]);

  /**
   * Handle status toggle change. Clears sub-status filters when phase changes.
   */
  const handleStatusChange = (newStatus: FulfillmentOrderPhase | 'all') => {
    if (newStatus === 'all') {
      navigateFn({
        search: {
          ...searchParameters,
          page: undefined,
          status: undefined,
          subStatuses: undefined,
        },
      });
    } else {
      navigateFn({
        search: {
          ...searchParameters,
          page: undefined,
          status: newStatus,
          subStatuses: undefined,
        },
      });
    }
  };

  /**
   * Handle modifier filter change.
   */
  const handleModifierChange = (subStatuses: FulfillmentOrderSubstatus[]) => {
    navigateFn({
      search: {
        ...searchParameters,
        page: undefined,
        subStatuses: subStatuses.length > 0 ? subStatuses : undefined,
      },
    });
  };

  /**
   * Handle a change to the "from" location filter selection.
   */
  const handleChangeFromLocations = (
    locationIds: string[],
    customerIds: string[],
    companyTypes: LocationCompanyType[]
  ) => {
    navigateFn({
      search: {
        ...searchParameters,
        page: undefined,
        fromLocations: locationIds.length > 0 ? locationIds : undefined,
        fromCustomerLocations: customerIds.length > 0 ? customerIds : undefined,
        fromLocationCompanyTypes:
          companyTypes.length > 0 ? companyTypes : undefined,
      },
    });
  };

  /**
   * Handle a change to the "to" location filter selection.
   */
  const handleChangeToLocations = (
    locationIds: string[],
    customerIds: string[],
    companyTypes: LocationCompanyType[]
  ) => {
    navigateFn({
      search: {
        ...searchParameters,
        page: undefined,
        toLocations: locationIds.length > 0 ? locationIds : undefined,
        toCustomerLocations: customerIds.length > 0 ? customerIds : undefined,
        toLocationCompanyTypes:
          companyTypes.length > 0 ? companyTypes : undefined,
      },
    });
  };

  const onPageChange = (pageNumber: number) => {
    navigateFn({
      search: { ...searchParameters, page: pageNumber },
    });
  };

  /**
   * Handle search input changes.
   */
  const handleChangeQuery: ChangeEventHandler<HTMLInputElement> = (event) => {
    updateQuery(event.target.value);
  };

  /**
   * Clear the search query.
   */
  const handleClearQuery = () => {
    if (searchContainer.current) {
      searchContainer.current.value = '';
    }
    updateQuery('');
  };

  const pageNumber = searchParameters.page ?? 1;
  const subStatuses = searchParameters.subStatuses ?? [];

  const fromLocationIds = searchParameters.fromLocations ?? [];
  const fromCustomerIds = searchParameters.fromCustomerLocations ?? [];
  const fromCompanyTypes = searchParameters.fromLocationCompanyTypes ?? [];
  const toLocationIds = searchParameters.toLocations ?? [];
  const toCustomerIds = searchParameters.toCustomerLocations ?? [];
  const toCompanyTypes = searchParameters.toLocationCompanyTypes ?? [];

  return (
    <Stack>
      <CreateOrderDialog
        isOpen={createDialog.isOpen}
        onClose={createDialog.close}
        defaultOrderType="fulfill"
        initialProjectID={props.initialProjectID}
        disableProjectChange={props.disableProjectChange}
      />

      {/* Action bar with filters, search, and create button - hidden when hideChrome is true */}
      {!props.hideChrome && (
        <>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            marginBottom={2}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <FilterToggleGroup
                value={currentStatus}
                options={statusFilterOptions}
                onChange={handleStatusChange}
                ariaLabel="fulfillment status filter"
              />
              <IconButton
                ref={infoButtonRef}
                size="small"
                onClick={infoTooltip.open}
                aria-label="Learn about fulfillment orders"
              >
                <InfoOutlined fontSize="small" />
              </IconButton>
            </Stack>

            {/* Educational tooltip explaining fulfillment orders and statuses */}
            <EducationalTooltip
              title="Fulfillment orders"
              isOpen={infoTooltip.isOpen}
              onClose={infoTooltip.close}
              anchorElement={infoButtonRef.current}
              placement="bottom-start"
            >
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2">
                    <strong>Fulfillment orders</strong> plan what needs to be
                    shipped, where it's going, and when it's promised to arrive.
                    Fulfillment orders themselves do not track items or
                    real-world movement. They serve as a set of business
                    instructions before actual shipments are created and
                    inventory is moved.
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Fulfillment order status
                  </Typography>
                  <Typography variant="body2">
                    <strong>Draft:</strong> Order is being prepared but no
                    shipments have been created yet.
                  </Typography>
                  {user.organization.usesThirdPartyFulfillment && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Awaiting 3PL:</strong> Order has been sent to a
                      third-party for fulfillment and is waiting for updates.
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Open:</strong> Shipments have been allocated against
                    the order but not yet shipped.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Shipped:</strong> Items have been shipped but not
                    yet delivered.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Delivered:</strong> Items have been delivered to the
                    destination location.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, mb: 0 }}>
                    <strong>Cancelled:</strong> The fulfillment order has been
                    cancelled and is no longer valid.
                  </Typography>
                </Box>
                <Typography variant="body2">
                  <Link
                    href="https://guide.hardfin.com/fulfill-and-deploy-transit/fulfillment-orders"
                    target="_blank"
                  >
                    Read more about fulfillment orders
                  </Link>
                </Typography>
              </Stack>
            </EducationalTooltip>

            <Button
              variant="contained"
              startIcon={<AddRounded fontSize="small" />}
              onClick={createDialog.open}
            >
              Add fulfillment order
            </Button>
          </Stack>

          <TableControlContainer paddingTop={0}>
            <Stack direction="row" marginRight="auto" spacing={1}>
              <AssetLocationFilterMenu
                label="From location"
                locationIds={fromLocationIds}
                customerIds={fromCustomerIds}
                companyTypes={fromCompanyTypes}
                onChange={handleChangeFromLocations}
              />
              <AssetLocationFilterMenu
                label="To location"
                locationIds={toLocationIds}
                customerIds={toCustomerIds}
                companyTypes={toCompanyTypes}
                onChange={handleChangeToLocations}
              />
              {(currentStatus === 'all' ||
                PHASES_WITH_MODIFIERS.has(currentStatus)) && (
                <FulfillmentModifierFilter
                  value={subStatuses}
                  onChange={handleModifierChange}
                />
              )}
            </Stack>
            <TextField
              size="small"
              inputRef={searchContainer}
              label="Search fulfillment orders"
              defaultValue={searchParameters.query}
              inputProps={{ maxLength: SEARCH_TERM_MAXIMUM_CHARACTERS }}
              onChange={handleChangeQuery}
              InputProps={{
                endAdornment: searchParameters.query && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleClearQuery}
                      edge="end"
                      size="small"
                    >
                      <ClearRounded />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: '100%', sm: '20rem' } }}
            />
          </TableControlContainer>
        </>
      )}

      {/* Table */}
      <SupportErrorBoundary message="Failed to load fulfillment orders">
        <Suspense fallback={<FulfillmentTableSkeleton />}>
          <FulfillmentTable
            filter={{
              status: currentStatus === 'all' ? undefined : currentStatus,
              subStatuses: subStatuses.length > 0 ? subStatuses : undefined,
              searchTerm: searchParameters.query,
              page: pageNumber,
              pageSize: 20,
              projectId: props.forProjectId,
              fromLocationIds:
                fromLocationIds.length > 0 ? fromLocationIds : undefined,
              fromCustomerIds:
                fromCustomerIds.length > 0 ? fromCustomerIds : undefined,
              fromLocationCompanyTypes:
                fromCompanyTypes.length > 0 ? fromCompanyTypes : undefined,
              toLocationIds:
                toLocationIds.length > 0 ? toLocationIds : undefined,
              toCustomerIds:
                toCustomerIds.length > 0 ? toCustomerIds : undefined,
              toLocationCompanyTypes:
                toCompanyTypes.length > 0 ? toCompanyTypes : undefined,
              sort: sort,
            }}
            sort={sort}
            onSort={onSort}
            onPageChange={onPageChange}
            hidePageBar={props.hideChrome}
            enableRowExpansion={props.enableRowExpansion}
          />
        </Suspense>
      </SupportErrorBoundary>
    </Stack>
  );
}
